import React from 'react';
import { Html } from '@react-three/drei';
import { useWorldStore } from '../state/worldStore';
import { heliBridge } from '../city/heliTour';
import { cutsceneBridge } from '../city/cutscene';

// WorldLabel — a drei <Html> overlay that knows to stand down in plan view.
//
// THE TRAP THIS EXISTS TO CLOSE.
//
// `<Html distanceFactor={n}>` scales its DOM node to fake perspective, and how
// it computes that scale depends on the camera type:
//
//   perspective   1 / (2 · tan(fov/2) · distance) · n   — shrinks with distance
//   orthographic  camera.zoom · n                       — distance is not in it
//
// An orthographic camera has no field of view and every object is the same size
// regardless of depth, so there is no distance term to use and drei falls back
// to zoom. That is defensible on its own terms and catastrophic here: BuildView
// fits the whole city to the viewport, which lands zoom around 6, so a nameplate
// authored at distanceFactor 9 renders at 54x. One NPC label covers the screen.
//
// It does not merely look wrong. That is a DOM element scaled ~60x, one per
// resident, and compositing them is what took the tab down — the freeze and the
// "replaced by a nameplate" were the same bug at two magnitudes.
//
// Hidden rather than rescaled, because a proximity label is a street-level
// affordance — it tells you who you are standing next to. From 400 units up
// there is nothing to be next to, so the honest fix is that it is not drawn.
//
// Use this instead of <Html> anywhere a distanceFactor is involved; the plain
// drei one is fine without it (drei uses a flat scale of 1 and the camera type
// never enters into it).
// THE OTHER TRAP: Z-INDEX. drei's <Html> defaults to a zIndexRange topping out
// at 16,777,271 — so an in-world bubble outranks every piece of HUD chrome no
// matter what z-index the chrome asks for (Khari, 2026-08-06: an NPC's banter
// bubble sitting on top of the open scene dock). No sane number on the panel
// side can win that, so the ceiling has to come down here.
//
// 30 keeps in-world labels above the canvas and below the interface: peer
// nameplates already pin themselves to 20, the in-hand phone to 40, and DOM
// chrome — SceneDock at 72, the mic dock, the phone — sits above all of it.
// A label belongs to the WORLD; anything you can type into belongs on top of it.
const WORLD_LABEL_Z = [30, 0];

/**
 * A NAME YOU CAN CLICK (2026-11-02). Khari: "To activate her, I have to click on
 * her body. I should be able to click on her green name as well. This should be
 * the case for all models, even if not in a casted role."
 *
 * A nameplate is DOM over the canvas with pointer-events off, so a click on it
 * fell straight through to nothing. Spread this onto the plate's own element
 * with the same handler the body has — `<div {...nameProps(onSelect, style)}>` —
 * and the name does what the body does. No handler = a plain label, exactly as
 * before. The WorldLabel wrapper keeps `pointerEvents: 'none'`; only the plate
 * itself takes the click, so the empty box around it never blocks a drag.
 * Like the 3D picking, it stands down in a helicopter (HeliDirector switches
 * r3f's picking off; a DOM plate never heard about that) and under a cutscene.
 */
export function nameProps(onSelect, style = {}) {
  if (!onSelect) return { style };
  return {
    role: 'button',
    style: { ...style, pointerEvents: 'auto', cursor: 'pointer' },
    onPointerDown: (e) => e.stopPropagation(),   // a press on the name is not the start of a drag-look
    onClick: (e) => {
      e.stopPropagation();
      if (heliBridge.aboard || cutsceneBridge.active) return;
      onSelect();
    },
  };
}

export default function WorldLabel({ zIndexRange = WORLD_LABEL_Z, ...props }) {
  const planView = useWorldStore((s) => s.planView);
  if (planView) return null;
  return <Html zIndexRange={zIndexRange} {...props} />;
}
